// Reading curriculum helpers — pure data leaf (no engine/render imports).
// Reading is the `correctness` focus practised with the falling-note highway
// faded; this maps reading progress to the suggested crutch opacity for apply
// songs. See docs/superpowers/specs/2026-06-13-reading-tutor-design.md.

import type { SkillId } from "../types";

/**
 * Suggested falling-note highway opacity (1 = full crutch, 0 = read unaided) for
 * a learner who has mastered the given skills — keyed off the furthest mastered
 * rung of the reading chain (landmarks → steps → skips). The graded crutch-fade:
 * the highway dims itself as reading improves. Unrelated skills are ignored.
 */
/** Until the staff carries the "when" anchor (beat-count overlay) and unambiguous
 *  silence (rest glyphs), the auto-fade stops here — an apply song never fully strips
 *  the highway. Drop to 0 when both staff items ship. See the escalation-ladder spec
 *  §"Staff prerequisites + the fade gate". (Reading *drills* still force 0 — that
 *  staff-only purity is what makes them reading drills; see readingViewOnLoad.) */
export const FULL_FADE_FLOOR = 0.33;

export function suggestedHighwayOpacity(mastered: Iterable<SkillId>): number {
  const set = mastered instanceof Set ? mastered : new Set(mastered);
  if (set.has("read-skips")) return FULL_FADE_FLOOR;
  if (set.has("read-steps")) return 0.33;
  if (set.has("read-landmarks")) return 0.66;
  return 1;
}

/** A reading drill teaches a `read-*` skill — the optional reading branch. */
export function isReadingDrill(teaches: readonly SkillId[] | undefined): boolean {
  return (teaches ?? []).some((id) => id.startsWith("read-"));
}

/** The highway crutch + staff state a loaded entry should default to. */
interface ReadingView {
  /** Falling-note highway opacity (1 = full crutch, 0 = read unaided). */
  highwayOpacity: number;
  /** Reading drills are read from the staff, so it must be shown. */
  forceStaff: boolean;
}

/**
 * The reading view to apply when an entry is loaded. A **reading drill** is
 * always staff-only (highway faded to 0 — the crutch would defeat the drill, so
 * progress is irrelevant); every **other** song (incl. an untagged import) gets
 * the graded crutch suggested by the learner's reading progress, leaving the
 * staff toggle untouched (the learner can still override either).
 * See docs/superpowers/specs/2026-06-13-reading-tutor-design.md §4.2 / §4.4.
 */
export function readingViewOnLoad(
  teaches: readonly SkillId[] | undefined,
  mastered: Iterable<SkillId>,
): ReadingView {
  if (isReadingDrill(teaches)) return { highwayOpacity: 0, forceStaff: true };
  return { highwayOpacity: suggestedHighwayOpacity(mastered), forceStaff: false };
}
